import { UploadStatus } from "@/shared/enums/upload-status";
import { useEffect, useMemo, useState } from "react";

export interface UploadProgressItem {
	id: string;
	fileName: string;
	status: UploadStatus;
	datasetId: string;
}

interface UseUploadProgressToastProps {
	uploads: UploadProgressItem[];
	onClose: () => void;
	autoHide: boolean;
}

interface UseUploadProgressToastReturn {
	isVisible: boolean;
	activeUploads: UploadProgressItem[];
	completedCount: number;
	errorCount: number;
	handleManualClose: () => void;
}

/**
 * Hook para manejar la lógica del toast de progreso de uploads
 */
export const useUploadProgressToast = ({
	uploads,
	onClose,
}: UseUploadProgressToastProps): UseUploadProgressToastReturn => {
	const [isVisible, setIsVisible] = useState<boolean>(false);

	useEffect(() => {
		setIsVisible(uploads.length > 0);
	}, [uploads]);

	const handleManualClose = () => {
		onClose();
	};

	const activeUploads = useMemo(
		() =>
			uploads.filter(
				(u) =>
					u.status === UploadStatus.UPLOADING ||
					u.status === UploadStatus.ANALYZING ||
					u.status === UploadStatus.PENDING,
			),
		[uploads],
	);

	const completedCount = useMemo(
		() => uploads.filter((u) => u.status === UploadStatus.COMPLETED).length,
		[uploads],
	);

	const errorCount = useMemo(
		() =>
			uploads.filter(
				(u) =>
					u.status === UploadStatus.UPLOADING_ERROR ||
					u.status === UploadStatus.ANALYZING_ERROR,
			).length,
		[uploads],
	);

	return {
		isVisible,
		activeUploads,
		completedCount,
		errorCount,
		handleManualClose,
	};
};
